import {
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  ListItem,
  ScaleFactorProvider,
  SubTodoIndicator,
  useScaleFactor,
} from "arkh";
import React, { Suspense, useCallback, useMemo, useState } from "react";
import { MasonryImageGrid } from "../../../common/MasonryImageGrid";
import { useFragment, graphql, useLazyLoadQuery } from "react-relay";

import { TodoDetailImages_todo$key } from "./__generated__/TodoDetailImages_todo.graphql";
import { TodoDetailQuery } from "./__generated__/TodoDetailQuery.graphql";
import { useSubscriptionWithSkip } from "widgets/src/utils/useSubscriptionWithSkip";
import { TodoDetailSubscription } from "./__generated__/TodoDetailSubscription.graphql";
import { TodoEditNewMainCreationInfo } from "arkh/src/cross-platform/Todo/TodoEditNewMainCreationInfo";
import { FormattedMessage } from "react-intl";
import { TodoDetailContent_todo$key } from "./__generated__/TodoDetailContent_todo.graphql";
import { TodoDetailContentMain_todo$key } from "./__generated__/TodoDetailContentMain_todo.graphql";
import { TodoDetailTimeline } from "./TodoDetailTimeline";
import { TodoToolbarEdit, TodoContact } from "widgets";
import { ToolbarFeature } from "widgets/src/Todo/TodoToolbar/TodoToolbarItem";
import { TVTodoPopovers } from "./TodoPopovers/TVTodoPopovers";
import { TodoDetailToolbar_todo$key } from "./__generated__/TodoDetailToolbar_todo.graphql";
import { RichTextViewer } from "@/src/components/RichText";
import { TodoDetailSubTodos_todo$key } from "./__generated__/TodoDetailSubTodos_todo.graphql";
import { TVTodoSubListItem } from "./TVTodoSubListItem";
import { LinkedProducts } from "../../../common/LinkedProducts";
import { SignalDetailFiles } from "../Signal/SignalDetailFiles";

export function TodoDetail(props: { todoId: string; isTouchMode: boolean }) {
  const result = useLazyLoadQuery<TodoDetailQuery>(
    graphql`
      query TodoDetailQuery($todoId: ID!) {
        todo(id: $todoId) {
          ...TodoDetailContent_todo
        }
      }
    `,
    {
      todoId: props.todoId,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );
  const todo = result.todo;

  useSubscriptionWithSkip<TodoDetailSubscription>(
    useMemo(
      () => ({
        variables: { todoId: props.todoId },
        subscription: graphql`
          subscription TodoDetailSubscription($todoId: ID!) {
            todoChangedByIds(todoIds: [$todoId]) {
              ...TodoDetailContent_todo
            }
          }
        `,
      }),
      [props.todoId]
    )
  );

  if (!todo) {
    return null;
  }

  return <TodoDetailContent todo={todo} isTouchMode={true} />;
}

function TodoDetailContent(props: {
  todo: TodoDetailContent_todo$key;
  isTouchMode: boolean;
}) {
  const sc = useScaleFactor();

  // we split this in two and make sure to always load TodoDetailContentMain_todo in the list so that it is preloaded
  const _todo = useFragment(
    graphql`
      fragment TodoDetailContent_todo on Todo {
        ...TodoDetailContentMain_todo
        ...TodoDetailToolbar_todo
        ...TodoDetailTimeline_todo
        ...TVTodoPopovers_todo
        ...TodoEditNewMainCreationInfo
        ...TodoDetailImages_todo
        ...TodoDetailSubTodos_todo
        ...TodoContact_todo
      }
    `,
    props.todo
  );

  const initialLoadedTodo = useFragment<TodoDetailContentMain_todo$key>(
    graphql`
      fragment TodoDetailContentMain_todo on Todo {
        id
        title
        formattedBody
        body
        isIssue
        ...TodoEditNewMainCreationInfo
        ...TodoDetailImages_todo
        linkedProducts {
          ...LinkedProducts_products
        }
        files {
          ...SignalDetailFiles_files
        }
      }
    `,
    _todo
  );
  const issuePlaceholder =
    initialLoadedTodo.isIssue && initialLoadedTodo.title === "";

  return (
    <>
      <Box
        style={{ flex: 1, flexDirection: "row" }}
        color="surface-background-light"
        spaceHorizontal="medium"
        spaceVertical="medium"
      >
        <Box
          style={{
            width: "45%",
          }}
        >
          <ScaleFactorProvider value={sc}>
            <Box
              color="surface-primary"
              borderColor="border-strong"
              style={{
                borderWidth: 1,
                flexDirection: "column",
              }}
              cornerRadius={"large"}
            >
              <TodoEditNewMainCreationInfo todo={initialLoadedTodo} />
              <Box space="small" spaceHorizontal="medium">
                <AHText
                  type="title-small"
                  color={issuePlaceholder ? "text-secondary" : "text-primary"}
                >
                  {issuePlaceholder ? (
                    <FormattedMessage
                      defaultMessage="Issue"
                      description="Issue placeholder"
                    />
                  ) : (
                    initialLoadedTodo.title
                  )}
                </AHText>
              </Box>
              {initialLoadedTodo.body && !initialLoadedTodo.formattedBody ? (
                <>
                  <AHSeparator />
                  <Box space="small">
                    <AHText type="body-big" space="small">
                      {initialLoadedTodo.body}
                    </AHText>
                  </Box>
                </>
              ) : null}
              {initialLoadedTodo.formattedBody ? (
                <Box spaceHorizontal="medium" spaceVertical="medium">
                  <RichTextViewer data={initialLoadedTodo.formattedBody} />
                </Box>
              ) : null}

              <SignalDetailFiles files={initialLoadedTodo.files} />
              <LinkedProducts products={initialLoadedTodo.linkedProducts} />

              <Suspense fallback={null}>
                <TodoContact
                  todo={_todo}
                  boxProps={{
                    spaceHorizontal: "medium",
                    spaceTop: "medium",
                  }}
                />
                <Toolbar todo={_todo} />
              </Suspense>
              <Suspense fallback={null}>
                <TodoDetailSubTodos todo={_todo} />
              </Suspense>
            </Box>
            <Suspense fallback={null}>
              <TodoDetailTimeline
                todo={_todo}
                isTouchMode={props.isTouchMode}
              />
            </Suspense>
          </ScaleFactorProvider>
        </Box>
        <TodoDetailImages todo={initialLoadedTodo} />
      </Box>
    </>
  );
}
const editableFeatures = ["PRIORITY", "TAG", "ASSIGNEE", "TEAM"] as const;
function Toolbar(props: { todo: TodoDetailToolbar_todo$key }) {
  const [openFeature, setOpenFeature] = useState<ToolbarFeature | null>(null);
  const dismissFeature = useCallback(() => {
    setOpenFeature(null);
  }, [setOpenFeature]);

  const todo = useFragment(
    graphql`
      fragment TodoDetailToolbar_todo on Todo {
        ...TodoToolbarEdit_todo
        ...TVTodoPopovers_todo
      }
    `,
    props.todo
  );

  return (
    <>
      <Box spaceTop="medium">
        <TodoToolbarEdit
          todo={todo}
          editableFeatures={editableFeatures}
          openFeature={setOpenFeature}
          currentEmployeeId={undefined}
        />
      </Box>
      <Suspense fallback={null}>
        <TVTodoPopovers
          todo={todo}
          openFeature={openFeature}
          dismissFeature={dismissFeature}
        />
      </Suspense>
    </>
  );
}

function TodoDetailSubTodos(props: { todo: TodoDetailSubTodos_todo$key }) {
  const todo = useFragment(
    graphql`
      fragment TodoDetailSubTodos_todo on Todo {
        subTodos {
          id
          ...TVTodoSubListItem_todo
          status
        }
        ...TodoEditNewMainPreventDefault_todo
      }
    `,
    props.todo
  );
  const todoLength = todo?.subTodos?.length ?? 0;

  const subTodoStatuses =
    todo?.subTodos?.map((subTodo) => subTodo.status) ?? [];
  if (todoLength === 0) {
    return null;
  }
  return (
    <>
      <Box spaceTop="medium" spaceHorizontal="medium" spaceBottom="medium">
        <ListItem
          border="border-primary"
          isFirst
          right={
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <SubTodoIndicator statuses={subTodoStatuses} />
            </Box>
          }
        >
          <AHText
            selectable={false}
            size={11}
            font={AHTextFonts.BOLD}
            color="text-secondary"
          >
            <FormattedMessage
              defaultMessage="Subtodos"
              description="TodoDetailSubTodos title"
            />
          </AHText>
        </ListItem>

        {todo?.subTodos?.map((item, i, arr) => (
          <React.Fragment key={item.id}>
            <AHSeparator />
            <TVTodoSubListItem todo={item} isLast={i === arr.length - 1} />
          </React.Fragment>
        ))}
      </Box>
    </>
  );
}

function TodoDetailImages(props: { todo: TodoDetailImages_todo$key }) {
  const todo = useFragment(
    graphql`
      fragment TodoDetailImages_todo on Todo {
        images {
          size(minHeight: 699, minWidth: 699) {
            url
          }
        }
      }
    `,
    props.todo
  );

  const images = useMemo(
    () =>
      todo.images.flatMap((image) =>
        image.size?.url ? [{ url: image.size.url }] : []
      ),
    [todo.images]
  );

  return images.length > 0 ? (
    <Box style={{ flex: 1 }} spaceLeft="medium">
      <MasonryImageGrid images={images} />
    </Box>
  ) : null;
}
